/* eslint-disable no-use-before-define */
import { applyContainerQuery } from 'react-container-query';
import cx from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
// importing moment, rather than relying on
// the global moment
// breaks the formatting of Portuguese dates
// import moment from 'moment';

import BookingSummary from '../BookingSummary';
import Button from '../Button';
import containerQuery from '../../constants/containerQuery';
import containerQueryProps from '../../props/containerQueryProps';
import { getFormattedPrice } from '../../utils';
import ReservationSummaryImage from './ReservationSummaryImage';
import Rga from '../Rga';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';

import Icon from '../Icon';
import CalendarIcon from '../../svg/icons/ic_calendar-solid_20x20.svg';
import MapMarker from '../../svg/icons/map-marker.svg';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';
import PrePaid from '../../svg/icons/ic_prepaid_20x20.svg';
import TicketIcon from '../../svg/icons/ic_ticket_20x20.svg';

import './ReservationSummary.scss';

export const EVENT_TYPE = 'event';
export const RESERVATION_TYPE = 'reservation';

// '7:00pm'
export function getFormattedTimeHours({ day, time }) {
  const momentDate = moment(`${day} ${time}`, 'YYYY-MM-DD HH:mm:ss', true);
  if (!momentDate.isValid()) {
    return null;
  }
  return momentDate.format('h:mmA');
}

export function getTitle(eventName, venue) {
  return eventName || venue;
}

export function getType(isEvent) {
  if (isEvent) {
    return EVENT_TYPE;
  }
  return RESERVATION_TYPE;
}

export function renderBookOnBehalf(bookOnBehalfOf, name) {
  if (!bookOnBehalfOf) {
    return null;
  }
  return (
    <BookingSummary
      condensed
      guestName={name}
    />
  );
}

export function renderCategory(shouldRender, type) {
  if (!shouldRender || type !== EVENT_TYPE) {
    return null;
  }

  return (
    <TranslationProvider translationKey="event">
      <span className="ReservationSummary__category">Event</span>
    </TranslationProvider>
  );
}

export function renderDateTime(ts, withRating) {
  if (!ts) {
    return null;
  }
  return renderRow(
    <Icon component={CalendarIcon} name="calendar" />,
    renderTimeSlot(ts, withRating)
  );
}

export function renderImage(imageUrl, large = false, asCard = false) {
  if (!imageUrl) {
    return null;
  }

  return <ReservationSummaryImage backgroundImage={imageUrl} large={large} card={asCard} />;
}

export function renderLocation(location) {
  if (!location) {
    return null;
  }

  return renderRow(
    <i className="ResyIcon" aria-hidden="true"><MapMarker /></i>,
    <>{location}</>
  );
}

export function isNumber(value) {
  // eslint-disable-next-line no-restricted-globals
  return typeof value === 'number' && isFinite(value);
}

export function renderEventVerbiage(num, tableType, price, currencyCode) {
  const ppt = renderPricePerTicket(price, currencyCode);
  return `${`${num.toString()} x ${tableType}`} ${ppt || ''}`;
}

export function renderNumPeople(num, type, tableType, price, currencyCode, bookOnBehalfOf) {
  if (!num) {
    return null;
  }

  const icon = (type === 'event') ? <Icon component={TicketIcon} name="ticket" /> : <Icon component={PeopleIcon} name="people" />;

  return renderRow(
    icon,
    (type === 'event') ? renderEventVerbiage(num, tableType, price, currencyCode) : renderReservationVerbiage(num, tableType, bookOnBehalfOf)
  );
}

export function renderPartyOf(seats) {
  return getTranslation({ translationKey: 'number_guests', translationValues: { number: `${seats}` }, fallback: `${seats} Guests` });
}

export function renderPrepaid(price, currencyCode, type) {
  if (type === EVENT_TYPE) {
    return null;
  }
  if (!price) {
    return null;
  }
  return renderRow(
    <i className="ResyIcon" aria-hidden="true"><PrePaid /></i>,
    renderPricePerGuest(price, currencyCode)
  );
}

export function renderPricePerTicket(price, currencyCode) {
  if (!isNumber(price)) {
    return null;
  }

  return `${getFormattedPrice(price, currencyCode)} ${getTranslation({ translationKey: 'per_ticket', translationValues: null, fallback: 'per ticket' })}`;
}

export function renderPricePerGuest(price, currencyCode) {
  if (!isNumber(price)) {
    return null;
  }

  return (
    <span>
      <span>
        {getFormattedPrice(price, currencyCode)}
        &nbsp;
      </span>
      <TranslationProvider translationKey="per_guest">
        <span>per guest</span>
      </TranslationProvider>
    </span>
  );
}

export function renderReservationVerbiage(num, tableType, bookOnBehalfOf) {
  if (bookOnBehalfOf) {
    return renderPartyOf(num);
  }
  const tt = tableType ? `, ${tableType}` : '';
  return `${renderPartyOf(num)}${tt}`;
}

export function renderRGA(select, smallBreakpoint) {
  if (!select) {
    return null;
  }

  return renderRow(
    <Rga variant={smallBreakpoint ? 'default' : 'small'} />
  );
}

export function renderRow(icon, text) {
  return (
    <div className="ReservationSummary__row">
      {icon}
      <span className="ReservationSummary__row-text">{text}</span>
    </div>
  );
}

export function renderTimeSlot(ts, withRating = false) {
  if (!ts || !ts.day || !ts.time) {
    return null;
  }
  const momentDate = moment(`${ts.day} ${ts.time}`, 'YYYY-MM-DD HH:mm:ss', true);
  if (!momentDate.isValid()) {
    return null;
  }

  const date = momentDate.format('ddd, MMM D, YYYY');
  const time = momentDate.format('h:mm A');

  return (
    <span className="ReservationSummary__time-slot">
      {date}
      {' '}
      {withRating && <br />}
      {time}
    </span>
  );
}

export function renderTitle(eventName, venue, onVenueTitleClick) {
  const t = (
    <h2 className="ReservationSummary__title">
      {getTitle(eventName, venue)}
    </h2>
  );
  if (onVenueTitleClick) {
    return (
      <Button
        onClick={onVenueTitleClick}
        data-test-id="reservation_summary-button-title"
        variant="article"
      >
        {t}
      </Button>
    );
  }
  return t;
}

const ReservationSummary = ({
  bookOnBehalfOf,
  containerQuery: { sm },
  currencyCode,
  eventName,
  guestName,
  imageUrl,
  isGda,
  largeImage,
  largeText,
  location,
  onClick,
  onVenueTitleClick,
  price,
  seats,
  showCategory,
  smallBreakpoint,
  tableType,
  timeslot,
  venue,
  withRating,
}) => {
  const type = getType(!!eventName);

  const summaryContent = (
    <>
      {renderImage(imageUrl, largeImage, sm)}

      <div className="ReservationSummary__lock">
        {renderCategory(showCategory, type)}

        {renderTitle(
          eventName,
          venue,
          onVenueTitleClick
        )}

        {renderDateTime(timeslot, withRating)}

        {renderLocation(location)}

        {renderNumPeople(
          seats,
          type,
          tableType,
          price,
          currencyCode,
          bookOnBehalfOf
        )}

        {renderPrepaid(price, currencyCode, type)}

        {renderRGA(!!isGda, smallBreakpoint)}

        {renderBookOnBehalf(bookOnBehalfOf, guestName)}
      </div>
    </>
  );

  if (!onClick) {
    return (
      <div className={cx(
        'ReservationSummary',
        {
          ReservationSummary__large: largeText,
          ReservationSummary__sm: sm,
        }
      )}
      >
        {summaryContent}
      </div>
    );
  }

  return (
    <Button
      variant="no-style"
      onClick={onClick}
      className={cx(
        'ReservationSummary',
        {
          ReservationSummary__large: largeText,
          ReservationSummary__sm: sm,
        }
      )}
    >
      {summaryContent}
    </Button>
  );
};

ReservationSummary.propTypes = {
  bookOnBehalfOf: PropTypes.bool,
  containerQuery: containerQueryProps,
  currencyCode: PropTypes.string,
  eventName: PropTypes.string,
  guestName: PropTypes.string,
  imageUrl: PropTypes.string,
  isGda: PropTypes.bool,
  largeImage: PropTypes.bool,
  largeText: PropTypes.bool,
  location: PropTypes.string,
  onClick: PropTypes.func,
  onVenueTitleClick: PropTypes.func,
  price: PropTypes.number,
  seats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showCategory: PropTypes.bool,
  smallBreakpoint: PropTypes.bool,
  tableType: PropTypes.string,
  timeslot: PropTypes.shape({
    day: PropTypes.string,
    time: PropTypes.string,
  }),
  venue: PropTypes.string,
  withRating: PropTypes.bool,
};

ReservationSummary.defaultProps = {
  bookOnBehalfOf: false,
  containerQuery: {},
  currencyCode: undefined,
  eventName: '',
  guestName: undefined,
  imageUrl: undefined,
  isGda: false,
  largeImage: false,
  largeText: false,
  location: undefined,
  onClick: undefined,
  onVenueTitleClick: undefined,
  price: undefined,
  seats: undefined,
  showCategory: false,
  smallBreakpoint: false,
  tableType: undefined,
  timeslot: undefined,
  venue: '',
  withRating: false,
};

ReservationSummary.displayName = 'ReservationSummary';
export { ReservationSummary as Component };
export default applyContainerQuery(ReservationSummary, containerQuery);
